import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Box, Button, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent,
    DialogTitle, List, ListItemButton, ListItemText,
    Paper, Skeleton, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {type FsCategoryLine, useAdjustDraftStore, useFsAdjustStore} from '@/store/audit';
import userStore from '@/store/admin/userStore';
import FsSelectionDrawer from '@/pages/components/audit/adjustment/adjust/support/FsSelectionDrawer';
import {SnackbarField} from '@/components/common/SnackbarField';
import {removeAdjust, saveWithWorkingPapers} from '@/services/auditService/auditAdjustOrchestrate';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {useQueryClient} from '@tanstack/react-query';
import AdjustDrCrView from '@/pages/components/audit/adjustment/adjust/support/AdjustDrCrView';
import {
    RowSelectorDialog,
    mapCommonInvestmentOptionsToRows,
    mapToPaperSelectionRowsToRows,
} from '@/pages/components/audit/adjustment/adjust/support/RowSelector';
import {
    collectLineCodes,
    isCompanionHiddenCode,
    resolveInvPurchasesCompanionHiddenForUi,
    shouldHideAppendAnotherRecordForInvPurchases,
} from '@/pages/components/audit/adjustment/adjust/utils/companion-codes';
import {
    formatAmount,
} from '@/pages/components/audit/adjustment/adjust/utils/classifyRows';
import {
    resolveRemarkForEdit,
    resolveRemarkForSave,
    toDisplayRemark,
} from '@/pages/components/audit/adjustment/adjust/utils/remarkDisplayUtils';
import {
    getCommonInvestmentRowOptions,
    isIaOrAiaToPaperCode,
    isCommonInvestmentCode,
    isItlTaxiDepToPaper,
    isIssuedCapitalToPaperCode,
    isPlAeRenCode,
    isPlAeRenSubmenuFetchCode,
    isPpeCategoryFetchCode,
    isPpeOrDepToPaperCode,
    type CommonInvestmentRowOption,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/initial-data-factories';
import {
    computeAppendBalanceAdjustmentForCode,
    getOriginalEntryFromFsCategory,
    isNoWpDetailCode,
    resolveAppendEffectiveEntry,
    resolveFsNodeOriginalEntry,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {
    buildTaxSaleHeaderFromDraft,
    isTaxSaleBackendEditState,
    syncTaxSaleNodesFromPlTaxDetail,
    updatePlTaxSourceRowFromCompanion,
    type TaxSaleCompanionCode,
    type TaxSaleSyncResult,
} from '@/wp/tax/taxSelfAdjustment';
import {invalidateWpSpecialSyncQueries} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/invalidateWpSpecialSyncQueries';
import {notifyFsAdjustmentSilentRefresh} from '@/utils/fsAdjustmentRefresh';
import NoDirectorConfirmDialog from '@/adjustment/adjust/support/NoDirectorConfirmDialog';
import MpfSourceEmptyConfirmDialog from '@/adjustment/adjust/support/MpfSourceEmptyConfirmDialog';

/**
 * A dialog for adjustment operations with tabs.
 * Uses SplitTabsPanel to render Adj From and Adj To tabs dynamically based on scheduleCode.
 */
export default function AdjustTabsDialog() {
    // Subscribe to store state
    const open = useAdjustDraftStore((state) => state.adjustTabsDialogOpen);
    const setOpen = useAdjustDraftStore((state) => state.setAdjustTabsDialogOpen);
    const cleanAll = useAdjustDraftStore((state) => state.cleanAll);
    const fromPaper = useAdjustDraftStore((state) => state.fromPaper);
    const toPaper = useAdjustDraftStore((state) => state.toPaper);

    const header = useAdjustDraftStore((state) => state.header);
    const addToPaper = useAdjustDraftStore((state) => state.addToPaper);
    const removeToPaper = useAdjustDraftStore((state) => state.removeToPaper);
    const requestOpenWpCode = useAdjustDraftStore((state) => state.requestOpenWpCode);
    const getUnifiedDetail = useAdjustDraftStore((state) => state.getUnifiedDetail);
    const setUnifiedDetail = useAdjustDraftStore((state) => state.setUnifiedDetail);
    const syncFromPaperDetails = useAdjustDraftStore((state) => state.syncFromPaperDetails);
    const resetFromPaperDetailsSync = useAdjustDraftStore((state) => state.resetFromPaperDetailsSync);
    const detailRevision = useAdjustDraftStore((state) => state.detailRevision);
    const fromPaperDetailsSynced = useAdjustDraftStore((state) => state.fromPaperDetailsSynced);
    const headerLines = useAdjustDraftStore((state) => state.header?.lines);
    const fromPaperCodeForCompanion = useAdjustDraftStore((state) => state.fromPaper?.code);
    const enqueueToPaperAssembly = useAdjustDraftStore((state) => state.enqueueToPaperAssembly);
    const pendingUserAction = useAdjustDraftStore((state) => state.pendingUserAction);
    const toPaperAssemblyBusy = useAdjustDraftStore((state) => state.toPaperAssemblyBusy);
    const toPaperAssemblyStatus = useAdjustDraftStore((state) => state.toPaperAssemblyStatus);
    const assemblyExtraRowKeys = useAdjustDraftStore((state) => state.assemblyExtraRowKeys);
    const assemblyFlatEquityAdjustments = useAdjustDraftStore((state) => state.assemblyFlatEquityAdjustments);
    const consumeAssemblyExtraRowKeys = useAdjustDraftStore((state) => state.consumeAssemblyExtraRowKeys);
    const consumeAssemblyFlatEquityAdjustments = useAdjustDraftStore((state) => state.consumeAssemblyFlatEquityAdjustments);
    const clearAssembledCode = useAdjustDraftStore((state) => state.clearAssembledCode);
    const appendToPaperRecord = useAdjustDraftStore((state) => state.appendToPaperRecord);
    const recomputeToPaperAdjustments = useAdjustDraftStore((state) => state.recomputeToPaperAdjustments);
    const resolveToPaperUserAction = useAdjustDraftStore((state) => state.resolveToPaperUserAction);
    const cancelToPaperUserAction = useAdjustDraftStore((state) => state.cancelToPaperUserAction);
    const clearPendingUserAction = useAdjustDraftStore((state) => state.clearPendingUserAction);
    const draftHydratedFromBackend = useAdjustDraftStore((state) => state.draftHydratedFromBackend);
    const fromPaperSnapshot = useAdjustDraftStore((state) => state.fromPaper);
    const toPaperSnapshot = useAdjustDraftStore((state) => state.toPaper);
    const draftToPaperAdjustments = useAdjustDraftStore((state) => state.draftToPaperAdjustments);
    const draftSimpleAdjustments = useAdjustDraftStore((state) => state.draftSimpleAdjustments);
    const toPaperUserEditedCodes = useAdjustDraftStore((state) => state.toPaperUserEditedCodes);
    const fromPaperUserEdited = useAdjustDraftStore((state) => state.fromPaperUserEdited);

    const pendingAppendAdjustments = useAdjustDraftStore((state) => state.pendingAppendAdjustments);
    const remark = useAdjustDraftStore((state) => state.draftRemark);
    const setRemark = useAdjustDraftStore((state) => state.setDraftRemark);

    const drCrRenderPayload = useMemo(
        () => useAdjustDraftStore.getState().getDrCrRenderPayload(),
        [detailRevision, fromPaperSnapshot, toPaperSnapshot, draftToPaperAdjustments, draftSimpleAdjustments, toPaperUserEditedCodes, fromPaperUserEdited, pendingAppendAdjustments, remark],
    );

    // ??adjustmentMode = 4 (ADJUST_AUTO_GEN) ????????????? Close ????
    const isReadOnly = header?.adjustmentMode === AdjustmentMode.ADJUST_AUTO_GEN;

    // ??adjustmentMode = 2 (ADJUST_FS_TO_FS) ?? line ??code + adjustmentAmount ???
    const isFsToFsMode = header?.adjustmentMode === AdjustmentMode.ADJUST_FS_TO_FS;

    /** getAdjustmentWithWorkingPapers + ADJUST_SALE(5) + Tax FS codes：Tax Sale 编辑回显（仅此场景） */
    const isTaxSaleEditMode = isTaxSaleBackendEditState({
        draftHydratedFromBackend,
        header,
    });
    /** Tax Sale，或 INV↔PUR 限定配对 / 后端仅 INV+PUR+CI：不渲染 Append Another Record */
    const hideAppendAnotherRecord = isTaxSaleEditMode || shouldHideAppendAnotherRecordForInvPurchases({
        fromCode: fromPaper?.code,
        toPaperCodes: toPaper?.codes,
        headerLines: header?.lines,
        draftHydratedFromBackend,
    });

    const queryClient = useQueryClient();
    const [fsDrawerOpen, setFsDrawerOpen] = useState(false);

    // ??????? state????adjustDraftStore ??????
    // const [fromPaperDetail, setFromPaperDetail] = useState<any>(...)
    // const [toPaperDetails, setToPaperDetails] = useState<Record<string, any>>({});
    const [toPaperLine, setToPaperLine] = useState<Record<string, FsCategoryLine>>({});

    // Snackbar state
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'warning' | 'error' | 'success'>('success');

    // ?????????????
    const [isProcessing, setIsProcessing] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const toPaperLoading = toPaperAssemblyBusy
        || Object.values(toPaperAssemblyStatus).some((status) => status === 'loading');

    // ??BS-CA-CASH bankRow ?????????{ [code]: { [rowIndex]: { bankName?, accountNumber? } } }
    const [bankFieldErrors, setBankFieldErrors] = useState<Record<string, Record<number, {
        bankName?: string;
        accountNumber?: string
    }>>>({});

    // ??AdjustDrCrView ????????????? diff ??
    const [drCrBalanced, setDrCrBalanced] = useState(false);
    const drCrDiffRef = useRef<{ amount: number; side: 'Dr' | 'Cr' }>({amount: 0, side: 'Dr'});
    /** Append Another Record ???t??A??J adjustDraftStore.pendingAppendAdjustments */

    /** Append Another Record：side 为 Dr/Cr 合计较大的一侧（heavySide），补平侧为其对面 */
    const handleBalanceChange = useCallback((isBalanced: boolean, diffAmount?: number, diffSide?: 'Dr' | 'Cr') => {
        setDrCrBalanced(isBalanced);
        drCrDiffRef.current = {amount: diffAmount ?? 0, side: diffSide ?? 'Dr'};
    }, []);

    // ??Remove ?????????
    const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

    // ??CommonInvestment ??????????
    const [rowSelectionOpen, setRowSelectionOpen] = useState(false);
    const [pendingRowSelection, setPendingRowSelection] = useState<{
        code: string;
        adjustmentAmount: number;
        perClient: number;
    } | null>(null);
    const [rowOptions, setRowOptions] = useState<CommonInvestmentRowOption[]>([]);
    // ??toPaper ????????????????????????????????????????
    const [toPaperRowSelectionOpen, setToPaperRowSelectionOpen] = useState(false);
    const [pendingToPaperRowSelection, setPendingToPaperRowSelection] = useState<{
        code: string;
        parDetail: any;
        adjustmentAmount: number;
        rows: Array<{ arrayName: string; rowIndex: number; rowKey: string; perClient: number; row: any }>;
    } | null>(null);
    // ?????????????????Interest Income, Donation, Gain/Loss Exchange ???????????
    const [arraySelectionOpen, setArraySelectionOpen] = useState(false);
    const [pendingArraySelection, setPendingArraySelection] = useState<{
        code: string;
        codeName: string;
        parDetail: any;
        adjustmentAmount: number;
        perClient: number;
        options: Array<{ key: string; label: string }>;
    } | null>(null);

    // ??toPaper DIR/DTD ????? directorId ?????? Issued Capital
    const [noDirectorConfirmOpen, setNoDirectorConfirmOpen] = useState(false);
    // PL-AE-MPF 二级菜单源 WP 为空时确认跳转
    const [mpfSourceEmptyConfirmOpen, setMpfSourceEmptyConfirmOpen] = useState(false);
    const [mpfSourceEmptyMessage, setMpfSourceEmptyMessage] = useState('');
    const [mpfSourceEmptyRedirectCode, setMpfSourceEmptyRedirectCode] = useState('');

    // ???????????????? AdjustDrCrView ??????extraRowKeys
    const [initialExtraRowKeys, setInitialExtraRowKeys] = useState<Set<string>>(new Set());

    const staticPositionCodes = useAdjustDraftStore((state) => state.staticPositionCodes);
    const setStaticPositionCodes = useAdjustDraftStore((state) => state.setStaticPositionCodes);
    /** ?????? adjustment ??????????????? */
    const userDynamicCodesRef = useRef<Set<string>>(new Set());
    /** ???? Adjustment ???K header.lines ???M????P staticPositionCodes ??? */
    const isSubmittingRef = useRef(false);

    const closeSelectionDialogs = useCallback(() => {
        setRowSelectionOpen(false);
        setPendingRowSelection(null);
        setRowOptions([]);
        setToPaperRowSelectionOpen(false);
        setPendingToPaperRowSelection(null);
        setArraySelectionOpen(false);
        setPendingArraySelection(null);
        clearPendingUserAction();
    }, [clearPendingUserAction]);

    // store pendingUserAction ?? ????? UI ???
    useEffect(() => {
        if (!pendingUserAction) return;

        switch (pendingUserAction.kind) {
            case 'commonInvestmentSelection':
                setRowOptions(getCommonInvestmentRowOptions(pendingUserAction.code));
                setPendingRowSelection({
                    code: pendingUserAction.code,
                    adjustmentAmount: pendingUserAction.adjustmentAmount,
                    perClient: pendingUserAction.perClient,
                });
                setRowSelectionOpen(true);
                break;
            case 'arraySelection':
                setPendingArraySelection({
                    code: pendingUserAction.code,
                    codeName: pendingUserAction.codeName,
                    parDetail: pendingUserAction.parDetail,
                    adjustmentAmount: pendingUserAction.adjustmentAmount,
                    perClient: pendingUserAction.perClient,
                    options: pendingUserAction.options,
                });
                setArraySelectionOpen(true);
                break;
            case 'rowSelection':
                setPendingToPaperRowSelection({
                    code: pendingUserAction.code,
                    parDetail: pendingUserAction.parDetail,
                    adjustmentAmount: pendingUserAction.adjustmentAmount,
                    rows: pendingUserAction.rows,
                });
                setToPaperRowSelectionOpen(true);
                break;
            case 'noDirectorConfirm':
                setNoDirectorConfirmOpen(true);
                break;
            case 'mpfSourceEmptyConfirm':
                setMpfSourceEmptyMessage(pendingUserAction.message || '');
                setMpfSourceEmptyRedirectCode(pendingUserAction.redirectCode || '');
                setMpfSourceEmptyConfirmOpen(true);
                break;
        }
    }, [pendingUserAction]);

    useEffect(() => {
        if (assemblyExtraRowKeys.length === 0) return;
        const keys = consumeAssemblyExtraRowKeys();
        if (keys.length === 0) return;
        setInitialExtraRowKeys((prev) => {
            const next = new Set(prev);
            keys.forEach((key) => next.add(key));
            return next;
        });
    }, [assemblyExtraRowKeys, consumeAssemblyExtraRowKeys]);

    useEffect(() => {
        if (Object.keys(assemblyFlatEquityAdjustments).length === 0) return;
        const adjustments = consumeAssemblyFlatEquityAdjustments();
        if (Object.keys(adjustments).length === 0) return;
        Object.entries(adjustments).forEach(([code, amount]) => {
            useAdjustDraftStore.getState().setDraftToPaperAdjustment(code, amount);
        });
    }, [assemblyFlatEquityAdjustments, consumeAssemblyFlatEquityAdjustments]);

    // 仅 AdjustTabsDialog：INV→PUR 显式配对时 PL-COT-PUR 不参与伴生 hidden
    const companionHiddenCodes = useMemo(() => {
        const codes = collectLineCodes(headerLines, [fromPaperCodeForCompanion]);
        return resolveInvPurchasesCompanionHiddenForUi(
            codes,
            fromPaperCodeForCompanion,
            toPaper?.codes,
        );
    }, [headerLines, fromPaperCodeForCompanion, toPaper?.codes]);

    const hasPendingToPaperFetch = useMemo(() => {
        if (draftHydratedFromBackend) return false;
        if (!toPaper?.codes || toPaper.codes.length === 0) return false;
        return toPaper.codes.some((code) => {
            if (isCompanionHiddenCode(code, companionHiddenCodes)) return false;
            if (getUnifiedDetail(code)) return false;
            // 普通 NO_WP_DETAIL 不阻塞；REN submenu / plain REN field shell / PPE 分类 / IA·AIA / IC 需等组装
            if (!isNoWpDetailCode(code)) return true;
            const wpFetch = toPaper.wpFetchCodes?.[code]
                ?? Object.entries(toPaper.wpFetchCodes || {}).find(
                    ([k]) => String(k || '').trim().toUpperCase() === String(code || '').trim().toUpperCase(),
                )?.[1];
            const isPlainRenFieldShell = isPlAeRenCode(code) && !isPlAeRenSubmenuFetchCode(wpFetch);
            return (
                (isPlAeRenCode(code) && isPlAeRenSubmenuFetchCode(wpFetch))
                || isPlainRenFieldShell
                || (isPpeOrDepToPaperCode(code) && isPpeCategoryFetchCode(wpFetch))
                || isIaOrAiaToPaperCode(code)
                || isCommonInvestmentCode(code)
                || isItlTaxiDepToPaper(code, wpFetch)
                || isIssuedCapitalToPaperCode(code)
            );
        });
    }, [draftHydratedFromBackend, toPaper?.codes, toPaper?.wpFetchCodes, companionHiddenCodes, detailRevision, getUnifiedDetail]);

    // Sync fromPaper.adjustmentLine.details into unified detail storage (store-owned, idempotent)
    useEffect(() => {
        if (!open) {
            return;
        }

        if (draftHydratedFromBackend && fromPaperDetailsSynced) {
            setDataLoading(false);
            return;
        }

        if (!fromPaper?.code) {
            resetFromPaperDetailsSync();
            setDataLoading(false);
            return;
        }

        if (fromPaperDetailsSynced) {
            setDataLoading(false);
            return;
        }

        setDataLoading(true);
        syncFromPaperDetails();
        setDataLoading(false);
    }, [
        open,
        draftHydratedFromBackend,
        fromPaper?.code,
        fromPaper?.adjustmentLine?.details,
        fromPaperDetailsSynced,
        syncFromPaperDetails,
        resetFromPaperDetailsSync,
    ]);

    const toPaperCodesStr = useMemo(() => {
        return toPaper?.codes?.join(',') || '';
    }, [toPaper?.codes]);

    // toPaper 初始配对：仅新建调整且 toPaper 端点变化时重算；编辑已有记录由 updateLineAdjustment 维护
    useEffect(() => {
        if (draftHydratedFromBackend) return;
        if (header?.id) return;
        if (fromPaper?.code && toPaper?.codes?.length) {
            recomputeToPaperAdjustments();
        }
    }, [
        draftHydratedFromBackend,
        header?.id,
        fromPaper?.code,
        toPaperCodesStr,
        recomputeToPaperAdjustments,
    ]);

    useEffect(() => {
        if (!header?.id || draftHydratedFromBackend) return;
        resetFromPaperDetailsSync();
        useAdjustDraftStore.getState().setDraftRemark(resolveRemarkForEdit(header.remark, header.adjustmentMode));
    }, [header?.id, header?.remark, header?.adjustmentMode, draftHydratedFromBackend, resetFromPaperDetailsSync]);

    // Tabs 打开或 toPaper 端点变化时触发组装（勿随 draftHydratedFromBackend 翻转重复触发，以免覆盖用户编辑）
    useEffect(() => {
        if (!open || isReadOnly || draftHydratedFromBackend) return;
        if (!toPaper?.codes?.length) return;
        void enqueueToPaperAssembly(toPaper.codes);
    }, [
        open,
        isReadOnly,
        toPaperCodesStr,
        fromPaper?.annualId,
        fromPaper?.companyId,
        enqueueToPaperAssembly,
    ]);

    const handleRowSelectionConfirm = useCallback((binding: { rowId?: number; rowIndex?: number; rowKey?: string }) => {
        resolveToPaperUserAction({kind: 'commonInvestmentSelection', binding});
        closeSelectionDialogs();
    }, [resolveToPaperUserAction, closeSelectionDialogs]);

    const handleRowSelectionCancel = useCallback(() => {
        cancelToPaperUserAction('default_first_row');
        closeSelectionDialogs();
    }, [cancelToPaperUserAction, closeSelectionDialogs]);

    const handleToPaperRowSelectionConfirm = useCallback((binding: { rowId?: number; rowIndex?: number; rowKey?: string }) => {
        resolveToPaperUserAction({kind: 'rowSelection', binding});
        closeSelectionDialogs();
    }, [resolveToPaperUserAction, closeSelectionDialogs]);

    const handleToPaperRowSelectionCancel = useCallback(() => {
        cancelToPaperUserAction('default_first_row');
        closeSelectionDialogs();
    }, [cancelToPaperUserAction, closeSelectionDialogs]);

    const handleArraySelectionConfirm = useCallback((selectedArrayKey: string) => {
        resolveToPaperUserAction({kind: 'arraySelection', selectedArrayKey});
        closeSelectionDialogs();
    }, [resolveToPaperUserAction, closeSelectionDialogs]);

    const handleArraySelectionCancel = useCallback(() => {
        cancelToPaperUserAction('default_first_row');
        closeSelectionDialogs();
    }, [cancelToPaperUserAction, closeSelectionDialogs]);

    // ????????????????????store / local state / ref / cache
    const performCleanup = useCallback(() => {
        // 1. ?? adjustDraftStore?cleanAll ?? componentDetails??fromPaper??toPaper ???
        cleanAll();
        // 2. 清理本地 UI state（draft* 已由 cleanAll 重置）
        setToPaperLine({});
        setDataLoading(false);
        setIsProcessing(false);
        setRowSelectionOpen(false);
        setPendingRowSelection(null);
        setRowOptions([]);
        setToPaperRowSelectionOpen(false);
        setPendingToPaperRowSelection(null);
        setArraySelectionOpen(false);
        setPendingArraySelection(null);
        setRemoveConfirmOpen(false);
        setNoDirectorConfirmOpen(false);
        setMpfSourceEmptyConfirmOpen(false);
        setMpfSourceEmptyMessage('');
        setMpfSourceEmptyRedirectCode('');
        setBankFieldErrors({});
        setDrCrBalanced(false);
        drCrDiffRef.current = {amount: 0, side: 'Dr'};
        useAdjustDraftStore.getState().setDraftRemark('To reclassify the accounts');
        setInitialExtraRowKeys(new Set());
        setStaticPositionCodes(new Set());
        userDynamicCodesRef.current = new Set();
        // 4. ??? fsAdjustStore ??wpDetailsCache????????????????????????
        const fsState = useFsAdjustStore.getState();
        if (fsState.wpDetailsCache && Object.keys(fsState.wpDetailsCache).length > 0) {
            useFsAdjustStore.setState({wpDetailsCache: {}});
        }
    }, [cleanAll]);

    const requestClose = useCallback(() => {
        if (!useAdjustDraftStore.getState().adjustTabsDialogOpen) return;
        const active = document.activeElement;
        if (active instanceof HTMLElement) {
            active.blur();
        }
        setOpen(false);
    }, [setOpen]);

    const handleDialogExited = useCallback(() => {
        if (useAdjustDraftStore.getState().adjustTabsDialogOpen) return;
        performCleanup();
    }, [performCleanup]);

    const onClose = requestClose;

    const handleDialogClose = useCallback((_event: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        requestClose();
    }, [requestClose]);

    // ???????????????????????????????????????????????
    useEffect(() => {
        return () => {
            performCleanup();
        };
    }, [performCleanup]);

    // Handle FsSelectionDrawer confirm - add scheduleCode to toPaper
    // ??????????????????item ??adjustment ?????Dr/Cr ??
    const handleFsSelection = useCallback((args: {
        code: string;
        node: any;
        payload: Record<string, any>;
        wpFetchCode?: string;
    }) => {
        // WP_TO_WP：FsSelectionDrawer 已将 PL-COT-OI/CI 归一为 BS-CA-INV（含 node 元数据）
        let entry = args.node?.entry || null;
        const fsAdjustState = useFsAdjustStore.getState();
        const lineFromStore = fsAdjustState.getLineByCode(args.code);

        if (!entry) {
            entry = lineFromStore?.entry || null;
        }

        // ??? lineMeta?????node ???????????name, scheduleName, fsId ???
        // ????? node ????fallback ??store ???
        const lineMeta = {
            fsId: args.node?.fsId || lineFromStore?.fsId,
            name: args.node?.name || args.node?.alias || lineFromStore?.name,
            aliasEn: args.node?.aliasEn || (lineFromStore as any)?.aliasEn,
            nameEn: args.node?.nameEn || (lineFromStore as any)?.nameEn,
            scheduleName: args.node?.scheduleName || lineFromStore?.scheduleName,
            code: args.code,
            entry: entry,
        };

        // 差额来自 AdjustDrCrView：side = 金额较大的一侧（heavySide）
        const {amount: diffVal, side: heavySide} = drCrDiffRef.current;
        const fillSide = heavySide === 'Dr' ? 'Cr' : 'Dr';
        const originalEntry = getOriginalEntryFromFsCategory(
            args.code,
            resolveFsNodeOriginalEntry(args.code, entry) ?? 'Cr',
        );
        const balanceAmount = computeAppendBalanceAdjustmentForCode(
            args.code,
            fillSide,
            originalEntry,
            diffVal,
        );
        const effectiveEntry = Math.abs(balanceAmount) > 0.001
            ? resolveAppendEffectiveEntry(args.code, balanceAmount, originalEntry)
            : originalEntry;

        const wpFetchCode = String(args.wpFetchCode || args.payload?.wpFetchCode || '').trim();
        appendToPaperRecord(
            args.code,
            balanceAmount,
            effectiveEntry,
            lineMeta,
            wpFetchCode ? {wpFetchCode} : undefined,
        );
        setFsDrawerOpen(false);
    }, [appendToPaperRecord]);

    // ???????excludeFsIds ??payload - ????????
    const EMPTY_EXCLUDE_IDS = useMemo(() => [], []);
    const fsDrawerPayload = useMemo(() => ({
        companyId: fromPaper?.companyId,
        annualId: fromPaper?.annualId,
        title: 'Select Schedule to Add',
    }), [fromPaper?.companyId, fromPaper?.annualId]);

    // ?????onClose ????
    const handleFsDrawerClose = useCallback(() => {
        setFsDrawerOpen(false);
    }, []);

    // Handle endpoint depleted - clear assembled code so re-adding will trigger assembly
    const handleEndpointDepleted = useCallback((code: string) => {
        clearAssembledCode(code);
        setInitialExtraRowKeys((prev) => {
            const prevSet = prev as Set<string>;
            const next = new Set<string>();
            for (const key of prevSet) {
                if (!key.startsWith(`${code}:`)) next.add(key);
            }
            return next.size === prevSet.size ? prevSet : next;
        });
        removeToPaper(code);
    }, [clearAssembledCode, removeToPaper]);

    const applyTaxSaleSync = useCallback((sync: TaxSaleSyncResult) => {
        setUnifiedDetail('PL-TAX', sync.updatedPlTaxDetail);
        useAdjustDraftStore.setState((s) => {
            const {companionCode, companionFields, plTaxFields, updatedPlTaxDetail} = sync;
            const existingCompanionLine = s.toPaper?.adjustmentLines?.[companionCode];
            const nextStatic = new Set(s.staticPositionCodes);
            nextStatic.delete('PL-TAX');
            nextStatic.delete(companionCode);

            return {
                fromPaper: s.fromPaper ? {
                    ...s.fromPaper,
                    entry: plTaxFields.entry,
                    adjustment: plTaxFields.adjustment,
                    adjustmentLine: s.fromPaper.adjustmentLine ? {
                        ...s.fromPaper.adjustmentLine,
                        entry: plTaxFields.entry,
                        adjustment: plTaxFields.adjustment,
                        adjustmentAmount: plTaxFields.adjustmentAmount,
                        details: [updatedPlTaxDetail as any],
                    } : s.fromPaper.adjustmentLine,
                } : null,
                componentDetails: {
                    ...s.componentDetails,
                    'PL-TAX': {
                        code: 'PL-TAX',
                        detail: updatedPlTaxDetail,
                        lastUpdated: Date.now(),
                    },
                },
                draftSimpleAdjustments: {
                    ...s.draftSimpleAdjustments,
                    'PL-TAX': plTaxFields.adjustment,
                },
                toPaper: s.toPaper ? {
                    ...s.toPaper,
                    entries: {
                        ...(s.toPaper.entries || {}),
                        [companionCode]: companionFields.entry,
                    },
                    adjustmentLines: {
                        ...(s.toPaper.adjustmentLines || {}),
                        [companionCode]: {
                            ...(existingCompanionLine || {}),
                            entry: companionFields.entry,
                            adjustment: companionFields.adjustment,
                            adjustmentAmount: Math.abs(companionFields.adjustment),
                        },
                    },
                } : null,
                draftToPaperAdjustments: {
                    ...s.draftToPaperAdjustments,
                    [companionCode]: companionFields.adjustment,
                },
                staticPositionCodes: nextStatic,
                fromPaperUserEdited: true,
                detailRevision: s.detailRevision + 1,
            } as Partial<typeof s>;
        });
    }, [setUnifiedDetail]);

    /** ADJUST_SALE 编辑：非 DTD 方式同步 PL-TAX 与 companion 节点的 adjustment / entry */
    const handleTaxSaleAdjustmentChange = useCallback((
        code: string,
        _arrayName: string,
        _rowIndex: number,
        newAdjustment: number,
    ) => {
        const store = useAdjustDraftStore.getState();
        if (!isTaxSaleBackendEditState(store)) return;

        const normalizedCode = String(code || '').trim().toUpperCase();

        if (normalizedCode === 'BS-CL-TAXP' || normalizedCode === 'BS-CA-TAXR') {
            const plDetail = updatePlTaxSourceRowFromCompanion(
                store.getUnifiedDetail('PL-TAX'),
                normalizedCode as TaxSaleCompanionCode,
                newAdjustment,
            );
            if (plDetail) {
                store.setUnifiedDetail('PL-TAX', plDetail);
            }
        }

        const sync = syncTaxSaleNodesFromPlTaxDetail(
            store.getUnifiedDetail('PL-TAX'),
            {forTabsDialogEdit: true},
        );
        if (sync) {
            applyTaxSaleSync(sync);
        }
    }, [applyTaxSaleSync]);

    const handleTaxSaleAdjust = useCallback(async () => {
        if (isProcessing || !drCrBalanced) return;

        setIsProcessing(true);
        isSubmittingRef.current = true;
        await new Promise((resolve) => setTimeout(resolve, 0));
        try {
            const store = useAdjustDraftStore.getState();
            if (!isTaxSaleBackendEditState(store)) {
                throw new Error('Tax sale edit mode is not active');
            }
            store.setDraftRemark(remark);

            if (!store.header?.id) {
                throw new Error('Missing adjustment header');
            }

            const plTaxDetail = store.getUnifiedDetail('PL-TAX');
            const fsAdjustState = useFsAdjustStore.getState();
            const orchestrateHeader = buildTaxSaleHeaderFromDraft({
                header: store.header,
                plTaxDetail,
                getLineByCode: (code: string) => fsAdjustState.getLineByCode(code),
                preparedUserId: userStore.getCurrentUserId(),
                remark: resolveRemarkForSave(remark, store.header.remark, store.header.adjustmentMode),
            });

            if (!orchestrateHeader) {
                throw new Error('Failed to prepare tax sale adjustment');
            }

            const result = await saveWithWorkingPapers(orchestrateHeader);
            if (result) {
                setSnackbarMessage('Self adjustment saved successfully');
                setSnackbarSeverity('success');
                setOpenSnackbar(true);
                const cid = fromPaper?.companyId ?? header?.companyId;
                const aid = fromPaper?.annualId ?? header?.annualId;
                if (cid && aid) {
                    await invalidateWpSpecialSyncQueries(
                        queryClient,
                        cid,
                        aid,
                        'PL-TAX',
                        'TaxWp',
                    );
                }
                performCleanup();
                setOpen(false);
            } else {
                throw new Error('Save failed');
            }
        } catch (error) {
            setSnackbarMessage(`Failed to save adjustments: ${error}`);
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            isSubmittingRef.current = false;
            setIsProcessing(false);
        }
    }, [isProcessing, drCrBalanced, remark, performCleanup, setOpen, queryClient, header?.companyId, header?.annualId, fromPaper?.companyId, fromPaper?.annualId]);

    const handleAdjust = useCallback(async () => {
        if (isTaxSaleEditMode) {
            await handleTaxSaleAdjust();
            return;
        }

        if (isProcessing || !drCrBalanced) return;

        setIsProcessing(true);
        isSubmittingRef.current = true;
        await new Promise((resolve) => setTimeout(resolve, 0));
        try {
            const store = useAdjustDraftStore.getState();
            store.setDraftRemark(remark);

            const prepared = store.prepareSubmitHeader({
                userId: userStore.getCurrentUserId(),
                remark,
                mode: AdjustmentMode.ADJUST_WP_TO_WP,
            });
            if (!prepared.valid) {
                if (prepared.bankFieldErrors) {
                    setBankFieldErrors(prepared.bankFieldErrors);
                    setIsProcessing(false);
                    isSubmittingRef.current = false;
                    return;
                }
                throw new Error(prepared.errors.join('; ') || 'Prepare submit failed');
            }
            setBankFieldErrors({});

            // console.log('[AdjustTabsDialog] handleAdjust Json', prepared.header);

            const result = await saveWithWorkingPapers(prepared.header);
            if (result) {
                const currentMode = store.currentMode;
                const modeText = currentMode === AdjustmentMode.ADJUST_WP_TO_FS
                    ? 'WP to FS Opening'
                    : 'WP to WP';
                setSnackbarMessage(`${modeText} adjustment saved successfully`);
                setSnackbarSeverity('success');
                setOpenSnackbar(true);
                const cid = fromPaper?.companyId ?? header?.companyId;
                const aid = fromPaper?.annualId ?? header?.annualId;
                if (cid && aid) {
                    const fromCode = String(fromPaper?.code || '').trim().toUpperCase();
                    if (fromCode === 'BS-NCA-PPE' || fromCode === 'PL-AE-DEP') {
                        await invalidateWpSpecialSyncQueries(
                            queryClient,
                            cid,
                            aid,
                            fromCode,
                            'PropertyPlantEquipment',
                        );
                    }
                }
                performCleanup();
                setOpen(false);
            } else {
                throw new Error('Save failed');
            }
        } catch (error) {
            setSnackbarMessage(`Failed to save adjustments: ${error}`);
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            isSubmittingRef.current = false;
            setIsProcessing(false);
        }
    }, [isTaxSaleEditMode, handleTaxSaleAdjust, isProcessing, drCrBalanced, remark, performCleanup, setOpen, queryClient, header?.companyId, header?.annualId, fromPaper?.companyId, fromPaper?.annualId, fromPaper?.code]);
    /** Auto adjustment (ADJUST_AUTO_GEN): save remark only, amounts remain read-only */
    const handleSaveAutoRemark = useCallback(async () => {
        if (isProcessing || !header?.id || !isReadOnly) return;

        setIsProcessing(true);
        await new Promise(resolve => setTimeout(resolve, 0));
        try {
            const submitHeader = {
                id: header.id,
                companyId: header.companyId,
                annualId: header.annualId,
                adjustmentMode: AdjustmentMode.ADJUST_AUTO_GEN,
                refNo: header.refNo,
                status: header.status,
                remark: resolveRemarkForSave(remark, header.remark, header.adjustmentMode),
                preparedUserId: header.preparedUserId,
                lines: (header.lines || []).map((line: any) => ({
                    id: line.id,
                    headerId: header.id,
                    fsId: line.fsId,
                    currency: line.currency,
                    code: line.code || line.scheduleCode,
                    entry: line.entry,
                    adjustmentAmount: line.adjustmentAmount,
                    details: [],
                })),
            };

            const result = await saveWithWorkingPapers(submitHeader);
            if (result) {
                setSnackbarMessage('Remark saved successfully');
                setSnackbarSeverity('success');
                setOpenSnackbar(true);
                requestClose();
            } else {
                throw new Error('Save failed');
            }
        } catch (error: any) {
            setSnackbarMessage(error?.message || 'Failed to save remark');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setIsProcessing(false);
        }
    }, [header, remark, isReadOnly, isProcessing, requestClose]);

    // ??Handle remove button click - show confirmation dialog
    const handleRemove = useCallback(() => {
        const headerId = header?.id;
        if (!headerId || isProcessing) return;
        setRemoveConfirmOpen(true);
    }, [header?.id, isProcessing]);

    // ??Handle confirmed removal (after user confirms in dialog)
    const handleConfirmRemove = useCallback(async () => {
        const headerId = header?.id;
        if (!headerId || isProcessing) return;

        setRemoveConfirmOpen(false);
        setIsProcessing(true);
        try {
            await removeAdjust(headerId);

            setSnackbarMessage('Adjustment removed successfully');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);

            const cid = fromPaper?.companyId ?? header?.companyId;
            const aid = fromPaper?.annualId ?? header?.annualId;
            if (cid && aid) {
                notifyFsAdjustmentSilentRefresh(cid, aid);
            }
            requestClose();
        } catch (error) {
            setSnackbarMessage(`Failed to remove adjustment: ${error}`);
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setIsProcessing(false);
        }
    }, [header?.id, header?.companyId, header?.annualId, isProcessing, requestClose, fromPaper?.companyId, fromPaper?.annualId]);

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    // ??Phase 4: Handle removing a To endpoint from the pair
    const handleRemoveTo = useCallback((toIndex: number) => {
        const codes = toPaper?.codes;
        if (codes && toIndex >= 0 && toIndex < codes.length) {
            removeToPaper(codes[toIndex]);
        }
    }, [toPaper?.codes, removeToPaper]);

    // ??Handle inline row item name change in Dr/Cr view
    const handleRowItemNameChange = useCallback((code: string, arrayName: string, rowIndex: number, newName: string) => {
        const currentDetail = getUnifiedDetail(code);
        if (!currentDetail || typeof currentDetail !== 'object') return;

        const rowArray = currentDetail[arrayName];
        if (!Array.isArray(rowArray) || rowIndex >= rowArray.length) return;

        const updatedArray = [...rowArray];
        updatedArray[rowIndex] = {
            ...updatedArray[rowIndex],
            item: newName,
        };

        setUnifiedDetail(code, {
            ...currentDetail,
            [arrayName]: updatedArray,
        });
    }, [getUnifiedDetail, setUnifiedDetail]);

    // ??Handle bank field change (bankName / accountNumber) for BS-CA-CASH bankRow
    const handleBankFieldChange = useCallback((code: string, arrayName: string, rowIndex: number, field: 'bankName' | 'accountNumber', value: string) => {
        const currentDetail = getUnifiedDetail(code);
        if (!currentDetail || typeof currentDetail !== 'object') return;

        const rowArray = currentDetail[arrayName];
        if (!Array.isArray(rowArray) || rowIndex >= rowArray.length) return;

        const updatedArray = [...rowArray];
        updatedArray[rowIndex] = {
            ...updatedArray[rowIndex],
            [field]: value,
        };

        setUnifiedDetail(code, {
            ...currentDetail,
            [arrayName]: updatedArray,
        });

        // Clear field-level error for the edited field
        setBankFieldErrors(prev => {
            const codeErrors = prev[code];
            if (!codeErrors) return prev;
            const rowErrors = codeErrors[rowIndex];
            if (!rowErrors) return prev;
            const updated = {...rowErrors};
            delete updated[field];
            if (!updated.bankName && !updated.accountNumber) {
                const {[rowIndex]: _, ...restRows} = codeErrors;
                if (Object.keys(restRows).length === 0) {
                    const {[code]: __, ...restCodes} = prev;
                    return restCodes;
                }
                return {...prev, [code]: restRows};
            }
            return {...prev, [code]: {...codeErrors, [rowIndex]: updated}};
        });
    }, [getUnifiedDetail, setUnifiedDetail]);

    // ??ADJUST_FS_TO_FS (mode 2) / ADJUST_AUTO_GEN (mode 4): ??header.lines ???? code + adjustmentAmount ??? Dr/Cr ??
    const fsToFsData = useMemo(() => {
        if ((!isFsToFsMode && !isReadOnly) || !header?.lines || header.lines.length === 0) return null;

        const fsAdjustState = useFsAdjustStore.getState();
        const drItems: Array<{ code: string; name: string; adjustmentAmount: number }> = [];
        const crItems: Array<{ code: string; name: string; adjustmentAmount: number }> = [];
        let drTotal = 0;
        let crTotal = 0;

        for (const line of header.lines) {
            const code = String((line as any).code || (line as any).scheduleCode || '').trim();
            if (!code || isCompanionHiddenCode(code, companionHiddenCodes)) continue;

            const adjustmentAmount = Number(line.adjustmentAmount) || 0;
            if (Math.abs(adjustmentAmount) < 0.001) continue;

            // ??? name?????fsAdjustStore?fallback ??code
            const lineData = fsAdjustState.getLineByCode(code);
            const name = lineData?.name || lineData?.scheduleName || code;

            // ??? entry?line.entry > fsAdjustStore.entry???????adjustmentAmount ?????
            let entry = (line as any).entry || lineData?.entry || 'Dr';
            if (entry === 'Dr') {
                entry = adjustmentAmount < 0 ? 'Cr' : 'Dr';
            } else if (entry === 'Cr') {
                entry = adjustmentAmount < 0 ? 'Dr' : 'Cr';
            }

            const item = {code, name, adjustmentAmount: Math.abs(adjustmentAmount)};
            if (entry === 'Dr') {
                drItems.push(item);
                drTotal += item.adjustmentAmount;
            } else {
                crItems.push(item);
                crTotal += item.adjustmentAmount;
            }
        }

        const isBalanced = Math.abs(drTotal - crTotal) <= 0.01 && (drTotal > 0 || crTotal > 0);

        return {drItems, crItems, drTotal, crTotal, isBalanced};
    }, [isFsToFsMode, isReadOnly, header?.lines, companionHiddenCodes]);

    // WHY: 内容随行数自然增高；仅 paper 触达视口 maxHeight 时子表 overflow 才出现滚动条
    const dialogPaperSx = useMemo(() => ({
        width: 'min(calc(100vw - 32px), 1152px)',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100dvh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    }), []);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleDialogClose}
                fullWidth
                maxWidth={false}
                disableEscapeKeyDown
                disableRestoreFocus
                TransitionProps={{
                    onExited: handleDialogExited,
                }}
                sx={{
                    '& .MuiDialog-paper': dialogPaperSx,
                    '& .MuiDialogContent-root': {
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogTitle sx={{flexShrink: 0}}>
                    {isFsToFsMode ? 'FS Opening Adjustment' : 'Working Paper Adjustment'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{
                        flex: '1 1 auto',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        {(isFsToFsMode || isReadOnly) && fsToFsData ? (
                            // ADJUST_FS_TO_FS / ADJUST_AUTO_GEN: dialog 未达上限时表自然增高；达上限后表体滚动
                            <Box sx={{
                                flex: '1 1 auto',
                                minHeight: 0,
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                boxSizing: 'border-box',
                            }}>
                                <TableContainer
                                    component={Paper}
                                    sx={{flex: '1 1 auto', minHeight: 0, overflow: 'auto'}}
                                >
                                    <Table size="small" stickyHeader sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        // Opaque sticky head so scrolling rows do not bleed through action.hover alpha
                                        '& .MuiTableCell-stickyHeader': {
                                            backgroundColor: (theme) => (
                                                theme.palette.mode === 'dark'
                                                    ? theme.palette.grey[800]
                                                    : theme.palette.grey[100]
                                            ),
                                            zIndex: 3,
                                        },
                                    }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{py: 0.5, fontWeight: 600}}>
                                                    Schedule
                                                </TableCell>
                                                <TableCell align="center"
                                                           sx={{fontWeight: 600, width: 160, py: 0.5}}>Dr.</TableCell>
                                                <TableCell align="center"
                                                           sx={{fontWeight: 600, width: 160, py: 0.5}}>Cr.</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        {/* Dr section */}
                                        {fsToFsData.drItems.length > 0 && (
                                            <TableBody>
                                                {fsToFsData.drItems.map((item) => (
                                                    <TableRow key={item.code} hover>
                                                        <TableCell sx={{py: 0.5}}>
                                                            <Typography variant="body2" noWrap>
                                                                {item.name} <Typography component="span"
                                                                                        variant="caption"
                                                                                        color="text.secondary">({item.code})</Typography>
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{py: 0.5, width: 160}}>
                                                            <Typography
                                                                variant="body2">{formatAmount(item.adjustmentAmount)}</Typography>
                                                        </TableCell>
                                                        <TableCell sx={{py: 0.5, width: 160}}/>
                                                    </TableRow>
                                                ))}
                                                <TableRow><TableCell colSpan={3} sx={{py: 0.25}}/></TableRow>
                                            </TableBody>
                                        )}
                                        {/* Cr section */}
                                        {fsToFsData.crItems.length > 0 && (
                                            <TableBody>
                                                {fsToFsData.crItems.map((item) => (
                                                    <TableRow key={item.code} hover>
                                                        <TableCell sx={{py: 0.5}}>
                                                            <Typography variant="body2" noWrap>
                                                                {item.name} <Typography component="span"
                                                                                        variant="caption"
                                                                                        color="text.secondary">({item.code})</Typography>
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{py: 0.5, width: 160}}/>
                                                        <TableCell align="center" sx={{py: 0.5, width: 160}}>
                                                            <Typography
                                                                variant="body2">{formatAmount(item.adjustmentAmount)}</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow><TableCell colSpan={3} sx={{py: 0.25}}/></TableRow>
                                            </TableBody>
                                        )}
                                    </Table>
                                </TableContainer>

                                {/* Balance summary — fixed */}
                                <Box sx={{
                                    mt: 2,
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    flexShrink: 0,
                                }}>
                                    <Typography variant="body2">
                                        Dr Total: <strong>{formatAmount(fsToFsData.drTotal)}</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                        Cr Total: <strong>{formatAmount(fsToFsData.crTotal)}</strong>
                                    </Typography>
                                    {fsToFsData.isBalanced ? (
                                        <Chip icon={<CheckCircleOutlineIcon/>} label="Balanced" color="success"
                                              size="small" variant="outlined"/>
                                    ) : (fsToFsData.drTotal > 0 || fsToFsData.crTotal > 0) ? (
                                        <Chip icon={<ErrorOutlineIcon/>}
                                              label={`Diff: ${formatAmount(Math.abs(fsToFsData.drTotal - fsToFsData.crTotal))}`}
                                              color="error" size="small" variant="outlined"/>
                                    ) : null}
                                </Box>

                                {/* Remark: mode 4 editable; mode 2 read-only — fixed */}
                                {isReadOnly ? (
                                    <Box sx={{mt: 2, flexShrink: 0}}>
                                        <TextField
                                            label="Remark"
                                            multiline
                                            rows={2}
                                            fullWidth
                                            value={remark}
                                            onChange={(e) => useAdjustDraftStore.getState().setDraftRemark(e.target.value)}
                                            size="small"
                                            variant="outlined"
                                            placeholder="Add remark for this auto adjustment"
                                        />
                                    </Box>
                                ) : toDisplayRemark(header?.remark, header?.adjustmentMode) ? (
                                    <Box sx={{mt: 2, flexShrink: 0}}>
                                        <Typography variant="body2" color="text.secondary">
                                            Remark: {toDisplayRemark(header?.remark, header?.adjustmentMode)}
                                        </Typography>
                                    </Box>
                                ) : null}
                            </Box>
                        ) : (dataLoading || toPaperLoading || hasPendingToPaperFetch || rowSelectionOpen || toPaperRowSelectionOpen || arraySelectionOpen) ? (
                            <Box sx={{p: 2, minHeight: 240}}>
                                <Box sx={{display: 'flex', gap: 2, mb: 2}}>
                                    <Skeleton variant="rectangular" width={100} height={36}/>
                                    <Skeleton variant="rectangular" width={100} height={36}/>
                                </Box>
                                <Skeleton variant="rectangular" width="100%" height={160}/>
                            </Box>
                        ) : (drCrRenderPayload?.lines?.length ?? 0) >= 2 ? (
                            // AdjustDrCrView 表区随内容增高；Dialog 触达 maxHeight 后才 overflow 滚动
                            <Box sx={{
                                flex: '1 1 auto',
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}>
                                <AdjustDrCrView
                                    lines={drCrRenderPayload!.lines!}
                                    companyId={drCrRenderPayload!.companyId}
                                    annualId={drCrRenderPayload!.annualId}
                                    onToDetailLoaded={setUnifiedDetail}
                                    onAdjustmentChange={isTaxSaleEditMode ? handleTaxSaleAdjustmentChange : undefined}
                                    onItemNameChange={handleRowItemNameChange}
                                    onBankFieldChange={handleBankFieldChange}
                                    bankFieldErrors={bankFieldErrors}
                                    onEndpointDepleted={handleEndpointDepleted}
                                    onAddToPaper={hideAppendAnotherRecord ? undefined : () => {
                                        useAdjustDraftStore.getState().setPeriodLockIsAppend(true);
                                        setFsDrawerOpen(true);
                                    }}
                                    onBalanceChange={handleBalanceChange}
                                    readOnly={isReadOnly}
                                    taxSalePositiveOnlyEdit={isTaxSaleEditMode}
                                    remark={remark}
                                    onRemarkChange={setRemark}
                                    initialExtraRowKeys={initialExtraRowKeys}
                                />
                            </Box>
                        ) : (
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6}}>
                                <div style={{color: '#999'}}>
                                    No adjustment data available. Please configure fromPaper and toPaper.
                                </div>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{flexShrink: 0}}>
                    <Button onClick={onClose} disabled={isProcessing}>Close</Button>
                    {isReadOnly && (
                        <Button
                            variant="contained"
                            onClick={handleSaveAutoRemark}
                            disabled={isProcessing}
                            startIcon={isProcessing ? <CircularProgress size={20}/> : null}
                        >
                            {isProcessing ? 'Saving...' : 'Save'}
                        </Button>
                    )}
                    {!isReadOnly && !isFsToFsMode && (
                        <Button
                            variant="contained"
                            onClick={handleAdjust}
                            disabled={isProcessing || !drCrBalanced}
                            startIcon={isProcessing ? <CircularProgress size={20}/> : null}
                        >
                            {isProcessing ? 'Processing...' : 'Adjustment'}
                        </Button>
                    )}
                    {!isReadOnly && header?.id && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRemove}
                            disabled={isProcessing}
                        >
                            Remove
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* FsSelectionDrawer for adding new tabs */}
            <FsSelectionDrawer
                open={fsDrawerOpen}
                onClose={handleFsDrawerClose}
                payload={fsDrawerPayload}
                onConfirm={handleFsSelection}
                excludeFsIds={EMPTY_EXCLUDE_IDS}
                adjustmentMode={AdjustmentMode.ADJUST_WP_TO_WP}
            />

            {/* Snackbar for feedback */}
            <SnackbarField
                open={openSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
            />

            {/* Remove Confirmation Dialog */}
            <Dialog
                open={removeConfirmOpen}
                onClose={() => setRemoveConfirmOpen(false)}
                maxWidth="xs"
            >
                <DialogTitle>Confirm Remove?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove this adjustment? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveConfirmOpen(false)} disabled={isProcessing}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmRemove}
                        disabled={isProcessing}
                        startIcon={isProcessing ? <CircularProgress size={20}/> : null}
                    >
                        {isProcessing ? 'Removing...' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* CommonInvestment Row Selection Dialog */}
            <RowSelectorDialog
                open={rowSelectionOpen}
                onClose={handleRowSelectionCancel}
                onConfirm={handleRowSelectionConfirm}
                onCancel={handleRowSelectionCancel}
                code={pendingRowSelection?.code || ''}
                rows={mapCommonInvestmentOptionsToRows(rowOptions)}
                title="Select Adjustment Row"
            />

            {/* toPaper Row Selection Dialog - ???????????????????? */}
            <RowSelectorDialog
                open={toPaperRowSelectionOpen}
                onClose={handleToPaperRowSelectionCancel}
                onConfirm={handleToPaperRowSelectionConfirm}
                onCancel={handleToPaperRowSelectionCancel}
                code={pendingToPaperRowSelection?.code || ''}
                rows={pendingToPaperRowSelection ? mapToPaperSelectionRowsToRows(pendingToPaperRowSelection.rows) : []}
                description="Multiple rows found in the working paper. Please select which row to apply the adjustment to:"
            />

            {/* ?????????? - Interest Income, Donation, Gain/Loss Exchange ??????????*/}
            <Dialog
                open={arraySelectionOpen}
                onClose={handleArraySelectionCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Select
                    Category{pendingArraySelection ? ` for ${pendingArraySelection.codeName}` : ''}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        Please select which category to add the adjustment row:
                    </Typography>
                    <List>
                        {pendingArraySelection?.options.map((option) => (
                            <ListItemButton
                                key={option.key}
                                onClick={() => handleArraySelectionConfirm(option.key)}
                                sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1}}
                            >
                                <ListItemText primary={option.label}/>
                            </ListItemButton>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleArraySelectionCancel}>Use Default</Button>
                </DialogActions>
            </Dialog>

            <NoDirectorConfirmDialog
                open={noDirectorConfirmOpen}
                onDismiss={() => setNoDirectorConfirmOpen(false)}
                onCloseWp={onClose}
                onRequestOpenCode={requestOpenWpCode}
            />

            <MpfSourceEmptyConfirmDialog
                open={mpfSourceEmptyConfirmOpen}
                message={mpfSourceEmptyMessage}
                redirectCode={mpfSourceEmptyRedirectCode}
                onDismiss={() => {
                    setMpfSourceEmptyConfirmOpen(false);
                    setMpfSourceEmptyMessage('');
                    setMpfSourceEmptyRedirectCode('');
                    clearPendingUserAction();
                }}
                onCloseAll={onClose}
                onRequestOpenCode={requestOpenWpCode}
            />
        </>
    );
}

